import { Alert, Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { ResourcePage, ResourceCollection, StatusBadge, type ResourceCollectionState, ValueBadge } from '../../../design-system';
import { commonDraftValid, emptyCommonDraft, type CommonCatalogDraft } from './ReferenceCatalogFields';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, useReferenceLifecycle } from './ReferenceLifecycle';
import { JobProfileDetailDialog } from './JobProfileDetailDialog';
import { JobProfileEditorDialog, type JobProfileEditor } from './JobProfileEditorDialog';
import { JobProfileNotices } from './JobProfileNotices';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  replaceCatalogItem,
  replaceCatalogItems,
  sortCatalog,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  type ReferenceDataResource,
  type ReferenceSettingsCommonProps,
} from '../model/referenceData';
import { createReferenceBulkPort, referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type {
  ReferenceJobFamily,
  ReferenceJobProfile,
  ReferenceJobProfileChangeInput,
  ReferenceJobProfileCreateInput,
} from '../model/types';
export function JobProfileReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  familyResource,
  profileResource,
}: ReferenceSettingsCommonProps & {
  familyResource: ReferenceDataResource<ReferenceJobFamily>;
  profileResource: ReferenceDataResource<ReferenceJobProfile>;
}) {
  const families = useMemo(() => sortCatalog(familyResource.data ?? []), [familyResource.data]);
  const [profiles, setProfiles] = useState<ReferenceJobProfile[]>(profileResource.data ?? []);
  const [selectedProfile, setSelectedProfile] = useState<ReferenceJobProfile>();
  const [editor, setEditor] = useState<JobProfileEditor>();
  const [profileQuery, setProfileQuery] = useState('');
  const [profileStatus, setProfileStatus] = useState('');
  const [profileFamily, setProfileFamily] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  useEffect(() => setProfiles(sortCatalog(profileResource.data ?? [])), [profileResource.data]);
  const activeFamilies = useMemo(() => families.filter((family) => family.status === 'ACTIVE'), [families]);
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.jobProfiles(families), [families]);
  const bulkReferencePort = useMemo(() => createReferenceBulkPort('jobFamilyCode', families), [families]);
  const filteredProfiles = useMemo(
    () =>
      filterCatalog(profiles, profileQuery, profileStatus).filter(
        (profile) => !profileFamily || profile.jobFamily?.id === profileFamily,
      ),
    [profiles, profileQuery, profileStatus, profileFamily],
  );
  const familyFilterOptions = useMemo(
    () => [
      {
        label: '전체',
        value: '',
      },
      ...families.map((family) => ({
        label: family.descriptor,
        value: family.id,
      })),
    ],
    [families],
  );
  const profileColumns = useMemo<GridColDef<ReferenceJobProfile>[]>(
    () => [
      {
        field: 'descriptor',
        headerName: '직무',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 180,
      },
      {
        field: 'jobFamily',
        headerName: '소속 직군',
        width: 160,
        valueGetter: (_value, row) => row.jobFamily?.descriptor ?? '직군 미지정',
        renderCell: ({ row }) => row.jobFamily ? <ValueBadge label={row.jobFamily.descriptor} /> : '직군 미지정',
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [],
  );
  const updateProfile = (item: ReferenceJobProfile) => {
    setProfiles((current) => replaceCatalogItem(current, item));
    setSelectedProfile((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) profileResource.reload();
  };
  const profileLifecycle = useReferenceLifecycle<ReferenceJobProfile>({
    kindLabel: '직무',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceJobProfile(id, input as ReferenceJobProfileChangeInput, key),
    fixtureBlockers: {
      'job-profile-engineer': [
        {
          type: 'WORKER_JOB',
          count: 12,
        },
        {
          type: 'OPEN_CHANGE',
          count: 2,
        },
      ],
    },
    onChanged: updateProfile,
    onNotice: setNotice,
  });
  const openProfileCreate = () => {
    if (!activeFamilies.length) return;
    setEditor({
      mode: 'create',
      draft: {
        ...emptyCommonDraft(),
        jobFamilyId: activeFamilies[0].id,
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openProfileEdit = (item: ReferenceJobProfile) => {
    setSelectedProfile(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        jobFamilyId: item.jobFamily?.id ?? '',
        effectiveDate: item.status === 'SCHEDULED' ? item.effectiveFrom : todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeEditorDraft = <K extends keyof CommonCatalogDraft>(field: K, value: CommonCatalogDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const changeProfileFamily = (jobFamilyId: string) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              jobFamilyId,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !commonDraftValid(editor.draft, editor.mode) || !editor.draft.jobFamilyId || busy) return;
    const family = families.find((candidate) => candidate.id === editor.draft.jobFamilyId);
    if (!family) {
      setEditorError('선택한 직군을 찾을 수 없습니다. 직군 목록을 다시 확인해 주세요.');
      return;
    }
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceJobProfile>;
    if (editor.mode === 'create') {
      const payload: ReferenceJobProfileCreateInput = {
        descriptor: editor.draft.descriptor.trim(),
        jobFamily: {
          id: family.id,
        },
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `job-profile-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            jobFamily: {
              id: family.id,
              code: family.code,
              descriptor: family.descriptor,
            },
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceJobProfile(payload, key);
    } else {
      const payload: ReferenceJobProfileChangeInput = {
        version: editor.item!.version,
        descriptor: editor.draft.descriptor.trim(),
        jobFamily: {
          id: family.id,
        },
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            jobFamily: {
              id: family.id,
              code: family.code,
              descriptor: family.descriptor,
            },
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceJobProfile(editor.item!.id, payload, key);
    }
    const savedMode = editor.mode;
    void request
      .then(
        (saved) => {
          updateProfile(saved);
          setEditor(undefined);
          setNotice(`${saved.descriptor} 직무를 ${savedMode === 'create' ? '등록' : '변경'}했습니다.`);
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '직무 정보를 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const profileTableState: ResourceCollectionState = profileResource.loading
    ? {
        kind: 'loading',
        label: '직무 목록을 불러오는 중입니다.',
      }
    : profileResource.error
      ? {
          kind: 'error',
          message: profileResource.error,
          onRetry: profileResource.reload,
          retryLabel: '직무 목록 다시 시도',
        }
      : profiles.length === 0
        ? {
            kind: 'empty',
            title: '등록된 직무가 없습니다.',
            description: canWrite ? '직무 등록을 눌러 첫 직무를 추가해 주세요.' : '등록된 직무가 없습니다.',
          }
        : {
            kind: 'ready',
          };
  const profileCreateDisabled = familyResource.loading || Boolean(familyResource.error) || !activeFamilies.length;
  const profileCreateDisabledReason = familyResource.loading
    ? undefined
    : familyResource.error
      ? '직군 목록을 불러온 뒤 직무를 등록할 수 있습니다.'
      : '먼저 사용 중인 직군을 등록해 주세요.';
  return (
    <>
      <ResourcePage
        title="직무"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "직무 등록",
          onCreate: openProfileCreate,
          onBulkCreate: () => setBulkOpen(true),
          disabled: profileCreateDisabled,
          loading: familyResource.loading,
          disabledReason: profileCreateDisabledReason,
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          <JobProfileNotices
            canRead={canRead}
            familyError={familyResource.error}
            notice={notice}
            onRetryFamilies={familyResource.reload}
            onDismissNotice={() => setNotice(undefined)}
          />
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={profileTableState}
                rows={filteredProfiles}
                totalCount={profiles.length}
                columns={[...profileColumns, catalogActionsColumn({ canWrite, onInspect: setSelectedProfile, onEdit: openProfileEdit, onLifecycle: profileLifecycle.open })]}
                query={profileQuery}
                onQueryChange={setProfileQuery}
                searchPlaceholder="직무 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '직군',
                    value: profileFamily,
                    onChange: setProfileFamily,
                    options: familyFilterOptions,
                  },
                  {
                    label: '상태',
                    value: profileStatus,
                    onChange: setProfileStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openProfileEdit(row) : setSelectedProfile(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <JobProfileDetailDialog
        profile={selectedProfile}
        canWrite={canWrite}
        onClose={() => setSelectedProfile(undefined)}
        onEdit={() => selectedProfile && openProfileEdit(selectedProfile)}
        onLifecycle={(action) => selectedProfile && profileLifecycle.open(action, selectedProfile)}
      />

      <JobProfileEditorDialog
        editor={editor}
        busy={busy}
        editorError={editorError}
        activeFamilies={activeFamilies}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        onChange={changeEditorDraft}
        onFamilyChange={changeProfileFamily}
      />
      <ReferenceBulkCreateDialog<ReferenceJobProfile>
        open={bulkOpen}
        workflow={bulkWorkflow}
        referencePort={bulkReferencePort}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.jobProfile(row, families);
          const family = families.find((candidate) => candidate.id === payload.jobFamily.id);
          if (!family) return Promise.reject(new Error('사용 중인 직군을 찾을 수 없습니다.'));
          return fixtureMode
            ? Promise.resolve({
                id: `job-profile-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                jobFamily: { id: family.id, code: family.code, descriptor: family.descriptor },
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceJobProfile(payload, key);
        }}
        onCreated={(created) => {
          setProfiles((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) profileResource.reload();
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `직무 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `직무 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {profileLifecycle.dialog}
    </>
  );
}
